import Link from 'next/link';

import Line from 'components/Line';
import { withPageStaticProps } from 'utils/header';
import { getUrlAsTitle } from 'utils/utils';
import { Sitemap, SitemapPath, getSitemap } from '../server/services/Sitemap';

type Url = SitemapPath & {
  url: string;
};

type GroupedLinks = {
  [key: string]: LinkGroup;
};

type LinkGroup = {
  links: Url[];
  header: string;
};

const getTopLevelOfPath = (path: string) => {
  const parts = path.split('/').filter((part) => part.trim() !== '');

  return parts[0] ?? '/';
};

const getGroupHeader = (group: string, links: Url[]) => {
  const link = links.find(({ path }) => path === `/${group}`);

  return link?.title ?? getUrlAsTitle(group);
};

const getGroupedLinks = (links: Url[]) =>
  links.reduce((cache: GroupedLinks, link) => {
    const group = getTopLevelOfPath(link.path);

    if (!cache.hasOwnProperty(group)) {
      cache[group] = {
        links: [],
        header: getGroupHeader(group, links),
      };
    }

    cache[group]['links'].push(link);

    return cache;
  }, {});

const getPathsWithUrl = (pages: Sitemap) => {
  return pages.map((entry) => ({
    ...entry,
    url: `${process.env.NEXT_PUBLIC_LOCAL_URL}${entry.path}`,
  }));
};

const withSinglePagesRegrouped = (groupedLinks: GroupedLinks) => {
  const entries = Object.entries(groupedLinks);

  const regroupedLinks = entries.reduce(
    (acc, [directory, group]) => {
      if (group.links.length === 1) {
        acc.topLevelPages.links.push(group.links[0]);
      } else {
        acc[directory] = group;
      }

      return acc;
    },
    {
      topLevelPages: {
        header: 'Pages',
        links: [],
      },
    } as GroupedLinks,
  );

  return regroupedLinks;
};

type Props = {
  groupedLinks: GroupedLinks;
};

export default function SiteMap({ groupedLinks }: Props) {
  return (
    <div className="m-section-hero container">
      <h1 className="text-h1-display">Sitemap</h1>
      <Line />
      <div className="grid grid-cols-12 sm:gap-32">
        {Object.entries(groupedLinks).map(([directory, group], index) => (
          <div key={index} className="col-span-12 space-y-16 sm:col-span-6 md:col-span-4 lg:col-span-3">
            <h2 key={directory} className="text-h4-display">
              {group.header}
            </h2>

            <ul>
              {group.links.map(({ url, title }) => (
                <li key={url} className="text-sm">
                  <Link href={url} prefetch={false} className={'tracker-link-sitemap-link-20210510-143621'}>
                    {title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export const getStaticProps = withPageStaticProps(async () => {
  const sitemap = await getSitemap();

  const groupedLinks = getGroupedLinks(getPathsWithUrl(sitemap));

  return {
    props: {
      groupedLinks: withSinglePagesRegrouped(groupedLinks),
    },
  };
});
